// Main-thread client for the compiler worker. Promise-based, with request ids
// so concurrent lint + compile calls don't cross wires, and a `lint` helper
// that debounces and cancels superseded keystroke requests.

import type { CompileRequest, CompileResponse, CompileResult, Target } from "../types";
import { DEFAULT_NETWORK } from "../network";
import type { Network } from "../network";

export interface CompileOpts {
  args?: string | null;
  target?: Target;
  allowUnproven?: boolean;
  network?: Network;
}

export class Compiler {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<
    number,
    { resolve: (r: CompileResult) => void; reject: (e: Error) => void }
  >();

  constructor() {
    this.worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (e: MessageEvent<CompileResponse>) => {
      const msg = e.data;
      const p = this.pending.get(msg.id);
      if (!p) return;
      this.pending.delete(msg.id);
      if (msg.ok) p.resolve(msg.result);
      else p.reject(new Error(msg.error));
    };
    // Surface a worker load/crash instead of letting requests hang forever.
    this.worker.onerror = (e) => {
      const err = new Error(`compiler worker error: ${e.message || "failed to load"}`);
      for (const p of this.pending.values()) p.reject(err);
      this.pending.clear();
    };
  }

  compile(source: string, opts: CompileOpts = {}): Promise<CompileResult> {
    const id = this.nextId++;
    const req: CompileRequest = {
      id,
      source,
      args: opts.args ?? null,
      target: opts.target ?? "fund",
      allowUnproven: opts.allowUnproven ?? false,
      network: opts.network ?? DEFAULT_NETWORK,
    };
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage(req);
    });
  }

  /** Fast diagnostics-only pass for on-keystroke linting. */
  check(source: string, args?: string | null): Promise<CompileResult> {
    return this.compile(source, { args, target: "check" });
  }

  dispose() {
    this.worker.terminate();
    this.pending.clear();
  }
}

// One compiler (one worker) for the whole app. A module singleton is the right
// lifetime AND sidesteps React StrictMode's mount/unmount/mount dry-run, which
// would otherwise terminate a per-component worker and never recreate it.
let singleton: Compiler | null = null;
export function getCompiler(): Compiler {
  if (!singleton) singleton = new Compiler();
  return singleton;
}
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    singleton?.dispose();
    singleton = null;
  });
}

/** A debouncer that runs `fn` after `ms` of quiet, cancelling earlier calls. */
export function debounce<A extends unknown[]>(fn: (...a: A) => void, ms: number) {
  let t: ReturnType<typeof setTimeout> | undefined;
  return (...a: A) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
}
